const API_BASE = import.meta.env.VITE_API_URL || '';

function readLastLoginMethod(): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const match = document.cookie.match(/(?:^|;\s*)better-auth\.last_used_login_method=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export class RequestError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export function appendQuery(
  path: string,
  params: Record<string, string | string[] | undefined | null>
): string {
  const url = new URL(path, 'http://perspectify.local');

  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const entry of value) {
        if (entry) {
          url.searchParams.append(key, entry);
        }
      }
    } else if (value) {
      url.searchParams.set(key, value);
    }
  }

  return `${url.pathname}${url.search}`;
}

async function fetchResponse(
  path: string,
  token?: string | null,
  init?: RequestInit
): Promise<Response> {
  const headers = new Headers(init?.headers);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const loginMethod = readLastLoginMethod();
  if (loginMethod && !headers.has('X-Perspectify-Login-Method')) {
    headers.set('X-Perspectify-Login-Method', loginMethod);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    let message = `Request failed: ${response.status}`;
    let body: unknown = null;

    try {
      const payload = (await response.json()) as { error?: string | { message?: string } };
      body = payload;
      const error = payload.error;
      if (typeof error === 'string' && error) {
        message = error;
      } else if (error && typeof error === 'object' && error.message) {
        message = error.message;
      }
    } catch {
      // Fall back to the status-based message when the body is not JSON.
    }

    throw new RequestError(response.status, message, body);
  }

  return response;
}

export async function fetchJson<T>(path: string, token?: string | null, init?: RequestInit): Promise<T> {
  return (await fetchResponse(path, token, init)).json() as Promise<T>;
}

export async function fetchBlob(path: string, token?: string | null): Promise<Blob> {
  // Session credentials belong only on this API's relative paths. Refuse
  // protocol-relative URLs, browser backslash aliases and redirect targets.
  if (!path.startsWith('/') || path.startsWith('//') || /[\\\u0000-\u0020]/.test(path)) {
    throw new Error('Invalid download URL.');
  }
  return (await fetchResponse(path, token, { redirect: 'error' })).blob();
}

export { API_BASE };
